import { getAddressStringForConsumer } from "@features/cx/shared/utils/filters";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { MapPin } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useConsumerList, useConsumerExport } from "../../hooks"; // Import the new hook
import { ConsumerFilters } from "../../types";
import { FilterPanel } from "../filters";
import { formatTerritoryAddress } from "@shared/utils/address";
import { HoverRevealText } from "@shared/components/HoverRevealText";

const statusBadge = (status: string) => {
  console.log("status", status);
  if (status === "ACTIVE")
    return (
      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
        Active
      </span>
    );
  if (status === "INACTIVE")
    return (
      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
        Inactive
      </span>
    );
  if (status === "PENDING")
    return (
      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
        Pending
      </span>
    );
  if (status === "DISCONNECTED")
    return (
      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
        Disconnected
      </span>
    );
  return (
    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
      {status}
    </span>
  );
};

export const ConsumerList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const remoteUtilityId = getRemoteUtilityId();
  const CURRENCY = useCurrency();

  // Use the export hook with current export filters

  // const columns: TableColumn<any>[] = [
  //   {
  //     key: "name",
  //     header: "Consumer",
  //     type: "custom",
  //     render: (_: any, row: any) => (
  //       <div>
  //         <div className="font-semibold">
  //           {row.firstName} {row.lastName}
  //         </div>
  //         <div className="text-xs text-muted-foreground">{row.email}</div>
  //       </div>
  //     ),
  //   },
  //   { key: "consumerNo", header: "Account Number", type: "text" },
  //   {
  //     key: "address",
  //     header: "Address",
  //     type: "custom",
  //     render: (_: any, row: any) => (
  //       <span className="flex items-center gap-1">
  //         <MapPin className="h-4 w-4 text-gray-400" />{" "}
  //         {getAddressStringForConsumer(row.territoryData.service, true)}
  //       </span>
  //     ),
  //   },
  //   {
  //     key: "statusDisplay",
  //     header: "Status",
  //     type: "custom",
  //     render: (value: any, row: any) => statusBadge(value),
  //   },
  //   {
  //     key: "outstandingBalance",
  //     header: "Balance",
  //     type: "custom",
  //     render: (value: number | undefined) => (
  //       <span
  //         className={
  //           !value || value === 0
  //             ? "text-green-600 font-medium"
  //             : "text-orange-500 font-medium"
  //         }
  //       >
  //         {CURRENCY} {value ?? "NA"}
  //       </span>
  //     ),
  //   },
  //   {
  //     key: "activateDate",
  //     header: "Activation Date",
  //     type: "custom",
  //     render: (value: string) => (
  //       <span className="flex items-center gap-1"> {value}</span>
  //     ),
  //   },
  // ];

  const columns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Consumer",
      type: "custom",
      render: (_: any, row: any) => (
        <div>
          <div className="font-semibold">
            {row.firstName} {row.lastName}
          </div>
          <div className="text-xs text-muted-foreground">{row.email}</div>
        </div>
      ),
    },
    { key: "consumerNo", header: "Account Number", type: "text" },
    {
      key: "address",
      header: "Address",
      type: "custom",
      render: (_: any, row: any) => {
        // Format address with line breaks
        const address = formatTerritoryAddress(row.territoryData, "billing");
        console.log("Formatted Address:", address);

        return (
          <span className="flex items-start gap-1">
            <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
            <HoverRevealText
              text={address || "No address configured"}
              maxLength={20} // adjust limit for tooltip trigger
            />
          </span>
        );
      },
    },

    {
      key: "statusDisplay",
      header: "Status",
      type: "custom",
      render: (value: any, row: any) => statusBadge(value),
    },
    {
      key: "ownershipDisplay",
      header: "Account Type",
      type: "custom",
      render: (value: any, row: any) => statusBadge(value),
    },
    {
      key: "outstandingBalance",
      header: "Balance",
      type: "custom",
      render: (value: number | undefined) => (
        <span
          className={
            !value || value === 0
              ? "text-green-600 font-medium"
              : "text-orange-500 font-medium"
          }
        >
          {CURRENCY} {value ?? "NA"}
        </span>
      ),
    },
    {
      key: "activateDate",
      header: "Activation Date",
      type: "custom",
      render: (value: string) => (
        <span className="flex items-center gap-1">{value}</span>
      ),
    },
  ];

  // Compute filters using useMemo instead of a function that calls hooks
  const filters = useMemo((): ConsumerFilters => {
    const urlFilters: ConsumerFilters = {
      page,
      limit: PAGE_SIZE,
      remoteUtilityId: Number(remoteUtilityId),
    };

    // Parse status array (should be codes)
    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== "ALL_STATUS") {
      urlFilters.status = statusParam;
    }

    // Parse category (single value, should be code)
    const categoryParam = searchParams.get("category");
    if (categoryParam && categoryParam !== "ALL_CATEGORIES") {
      urlFilters.category = categoryParam;
    }

    // Parse search query
    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  }, [page, searchParams, remoteUtilityId]);

  // Create export filters (without pagination)
  const exportFilters = useMemo((): ConsumerFilters => {
    const exportUrlFilters: ConsumerFilters = {
      remoteUtilityId: Number(remoteUtilityId),
    };

    // Parse status array (should be codes)
    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== "ALL_STATUS") {
      exportUrlFilters.status = statusParam;
    }

    // Parse category (single value, should be code)
    const categoryParam = searchParams.get("category");
    if (categoryParam && categoryParam !== "ALL_CATEGORIES") {
      exportUrlFilters.category = categoryParam;
    }

    // Parse search query
    const queryParam = searchParams.get("query");
    if (queryParam) {
      exportUrlFilters.search_data = queryParam;
    }

    return exportUrlFilters;
  }, [searchParams, remoteUtilityId]);

  // Update page when URL page param changes
  useEffect(() => {
    const pageParam = searchParams.get("page");
    if (pageParam) {
      setPage(parseInt(pageParam, 10));
    }
  }, [searchParams]);

  const { data, refetch } = useConsumerList(filters);
  const { exportCSV, isExporting } = useConsumerExport(exportFilters);

  useEffect(() => {
    refetch();
  }, []);

  const handleApplyFilters = (newFilters: Partial<ConsumerFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("status");
    params.delete("payment_status");
    params.delete("category");
    params.delete("page");

    // Add new filter params
    if (newFilters.status && newFilters.status.length > 0) {
      params.set("status", newFilters.status);
    }
    if (newFilters.category) {
      params.set("category", newFilters.category);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setPage(1);

    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("status");
    params.delete("payment_status");
    params.delete("category");
    params.delete("page");
    params.delete("query");

    setPage(1);
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
    setPage(newPage);
  };

  const handleSearch = (searchTerm: string, paramName: "search_data") => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    setPage(1);

    setSearchParams(params);
  };

  const actions: TableAction<any>[] = [
    {
      label: "View Details",
      onClick: (row: any) => {
        navigate(`/cx/consumer-management/${row.consumerNo}`);
      },
    },
    {
      label: "Record Payment",
      onClick: (row) => {
        navigate(`/cx/payments/create?consumerId=${row.id}&search=${row.consumerNo}`);
      },
    },
    {
      label: "Create Service Request",
      onClick: (row) => {
        navigate(`/cx/services/create?consumerId=${row.id}&search=${row.consumerNo}&source=account-management`);
      },
    },
  ];

  // Updated handleExport function
  const handleExport = () => {
    exportCSV();
  };

  return (
    <GenericTable
      data={data?.results || []}
      columns={columns}
      searchPlaceholder="Search by name, email, or ID..."
      searchParamName="query"
      searchDebounceMs={300}
      onSearch={handleSearch}
      onExport={handleExport}
      exportLoading={isExporting} // Pass the loading state
      rowKey="id"
      pagination={{
        currentPage: page,
        totalPages: Math.ceil((data?.count || 0) / PAGE_SIZE),
        totalItems: data?.count || 0,
        itemsPerPage: PAGE_SIZE,
        startItem: (page - 1) * PAGE_SIZE + 1,
        endItem: Math.min(page * PAGE_SIZE, data?.count || 0),
      }}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      filterPanel={
        <FilterPanel
          currentFilters={filters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
        />
      }
    />
  );
};
